<!-- 最新消息：/news -->
<template>
  <div class="layout_wrapper">
    <div class="container_wrapper">
      <h3 class="page_main_title">
        最新消息
      </h3>

      <TabMenu :model="categoryItems" />

      <div class="content">
        <div
          v-for="(item, idx) in currentContent.data"
          :key="idx"
          class="item_block"
          @click="toDetail(item)"
        >
          <div class="img_block">
            <picture>
              <source
                media="(min-width: 768px) and (max-width: 1024px)"
                :srcset="String(item.img_url
                  .replace(/^(.*).(png|jpg|jpeg|svg|gif)$/gm, '$1@2x.$2'))"
              >
              <source
                media="(max-width: 414px)"
                :srcset="item.img_url"
              >

              <img
                alt="news logo"
                class="menu_icon"
                :srcset="String(item.img_url)
                  .replace(/^(.*).(png|jpg|jpeg|svg|gif)$/gm, '$1@3x.$2')"
              >
            </picture>
          </div>

          <div class="info_block">
            <span
              v-show="isCategoryShow"
              class="tag_text"
            >
              {{ item?.category }}
            </span>

            <span class="date_time">
              {{ item.date }}
            </span>

            <div class="title_block">
              <p class="title">
                {{ item.title }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Paginator
        :rows="pageInfo.perPageLimit"
        :total-records="pageInfo.totalRecord"
        @page="onPage($event)"
      />
    </div>

    <component :is="dialogName" />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, nextTick } from 'vue';
import { onBeforeRouteUpdate } from 'vue-router';
import { AppDialog } from '@/components/Modal/';
import { setGaEvent } from '@/libs/global';
import { getNewsList } from '@/libs/API/apis';
import useCommon from '@/libs/globalComposition';
import TabMenu from 'primevue/tabmenu';
import Paginator from 'primevue/paginator';

export default {
  name: 'News',
  components: {
    TabMenu,
    AppDialog,
    Paginator
  },
  setup () {
    const { router, currentRoute, dialogName } = useCommon();
    const categoryTitle = ref('');
    const categories = ref([]);

    const newsContent = reactive({ data: {} });
    const currentContent = reactive({ data: {} });
    const pageInfo = reactive({
      currentPage: Number(currentRoute.params.page),
      totalRecord: 0,
      perPageLimit: 9
    });

    const categoryItems = computed(() => {
      const categoryArr = [];

      ['全部', ...categories.value].forEach((item, key) => {
        const page = categoryTitle.value === item ? pageInfo.currentPage : 1;

        categoryArr.push({ label: item, to: `/news/${encodeURI(item)}/${page}` });
      });

      return categoryArr;
    });
    const isCategoryShow = computed(() => {
      return currentRoute.params.type === '全部';
    });

    const getTabAction = (path = currentRoute.path) => {
      const [,, urlType, urlPage] = path.split('/');

      pageInfo.currentPage = Number(urlPage);
      categoryTitle.value = decodeURI(urlType);

      const tabPage = newsContent.data[categoryTitle.value]?.length;

      if (Object.keys(newsContent.data).indexOf(categoryTitle.value) === -1) {
        pageInfo.currentPage = 1;
        categoryTitle.value = Object.keys(newsContent.data)[0];

        router.replace(`/news/${encodeURI(categoryTitle.value)}/1`);
        return;
      } else if (!/\d+/gm.test(urlPage) || Number(urlPage) > tabPage) {
        router.replace(`/news/${encodeURI(categoryTitle.value)}/1`);
        pageInfo.currentPage = 1;

        return;
      }

      currentContent.data = newsContent.data[categoryTitle.value][pageInfo.currentPage - 1];
      pageInfo.totalRecord = tabPage * pageInfo.perPageLimit;

      activePage();
    };

    const toDetail = ({ category, title, id }) => {
      setGaEvent('news', { title });

      router.push(`/news_detail/${category}/${id}`);
    };

    const onPage = ($event) => {
      activePage();

      router.push(`/news/${encodeURI(categoryTitle.value)}/${$event.page + 1}`);
    };

    const activePage = () => {
      nextTick(() => {
        document.getElementsByClassName('p-paginator-page  p-highlight')[0]?.classList.remove('p-highlight');
        document.getElementsByClassName('p-paginator-page')[pageInfo.currentPage - 1]?.classList.add('p-highlight');
      });
    };

    const getNewsData = () => {
      getNewsList()
        .then((res) => {
          const allData = [];

          for (const [categoryName, categoryItems] of Object.entries(res)) {
            categories.value.push(categoryName);

            const sortedCategoryData = Object.entries(categoryItems)
              .map(([id, item]) => ({ id, ...item }));

            allData.push(...sortedCategoryData);
          }

          const allCategoryData = allData.sort((a, b) => Number(b.id) - Number(a.id));

          // 根據類別處理分頁
          const arrangeData = allCategoryData.reduce((acc, item) => {
            const { category } = item;

            if (!acc[category]) {
              acc[category] = [];
            }

            // 獲取當前類別的最後一頁
            let currentPage = acc[category][acc[category].length - 1];

            // 如果最後一頁不存在或已滿，創建一個新頁
            if (!currentPage || currentPage.length >= pageInfo.perPageLimit) {
              currentPage = [];
              acc[category].push(currentPage);
            }

            currentPage.push(item);

            return acc;
          }, {});

          newsContent.data = { ...arrangeData, 全部: chunkArray(allCategoryData) };

          getTabAction();
        });
    };

    const chunkArray = (list = [], limit = 9) => {
      const groupData = [];

      for (let i = 0; i < list.length; i += limit) {
        groupData.push(list.slice(i, i + limit));
      }

      return groupData;
    };

    onMounted(() => {
      onBeforeRouteUpdate((to, from) => {
        getTabAction(to.path);
      });

      getNewsData();
    });

    return {
      dialogName,
      categoryItems,
      isCategoryShow,
      currentContent,
      newsContent,
      pageInfo,
      getTabAction,
      onPage,
      toDetail
    };
  }
};
</script>

<style scoped lang="scss">
@import '@/scss/common/common.scss';
@import '@/scss/common/tabs.scss';
@import '@/scss/common/layout.scss';

::v-deep(.p-paginator) {
  .p-paginator-first {
    display: none;
  }

  .p-paginator-last {
    display: none;
  }

  .p-paginator-next {
    margin: 0px 0px 0px 10px;

    .p-paginator-icon {
      color: $black;
      font-size: 23px;
      height: 23px;
      margin: 0px;
      width: 23px;
    }
  }

  .p-paginator-prev {
    margin: 0px 10px 0px 0px;

    .p-paginator-icon {
      color: $black;
      font-size: 23px;
      height: 23px;
      margin: 0px;
      width: 23px;
    }
  }

  .p-paginator-pages {
    .p-paginator-page {
      @include font-size-bold-16;

      background: transparent;
      color: $black;
      margin: 0px 3px;

      &.p-highlight {
        background: $black;
        border-radius: 50%;
        color: $white;
      }

      &:not(.p-highlight):hover {
        background: transparent;
        border-color: transparent;
        color: $black;
      }

      &.p-link:focus {
        box-shadow: none;
        outline: none;
        outline-offset: 0;
      }
    }
  }

  &.p-paginator-first:not(.p-disabled):not(.p-highlight):hover,
  &.p-paginator .p-paginator-prev:not(.p-disabled):not(.p-highlight):hover,
  &.p-paginator .p-paginator-next:not(.p-disabled):not(.p-highlight):hover,
  &.p-paginator .p-paginator-last:not(.p-disabled):not(.p-highlight):hover {
    background: transparent;
    border-color: transparent;
    color: transparent;
  }
}

.layout_wrapper {
  .content {
    display: flex;
    flex-wrap: wrap;
    margin: 30px -14.5px 0px -14.5px;

    .item_block {
      cursor: pointer;
      margin-bottom: 50px;
      padding: 0px 14.5px;
      width: calc(100% / 3);

      &:hover .info_block .title {
        background-position: bottom 5px right;
        background-size: 100% 30px;
      }

      .img_block {
        background: #d8d8d8;
        height: 232px;
        margin-bottom: 20px;

        img {
          display: block;
          height: 100%;
          width: 100%;
        }
      }

      .info_block {
        .title_block {
          margin: 10px 0px 0px 0px;
          max-height: 64px;
          overflow: hidden;
        }

        .title {
          background: linear-gradient(to top, rgba($news_color, 1) 50%, transparent 50%);
          background-position: top 16px right;
          background-repeat: no-repeat;
          background-size: 100% 200%;
          display: inline;
          font-size: 22px;
          font-weight: 700;
          line-height: 32px;
          position: relative;
          transform-origin: bottom center;
          transition: all 0.3s ease-in-out;
        }

        .date_time {
          @include font-size-bold-18;

          color: $mono_80;
          margin: 10px 0px 0px 0px;
        }

        .tag_text {
          @include font-size-bold-18;

          background: $news_color;
          margin: 0px 10px 0px 0px;
          padding: 4px 8px;
        }
      }
    }
  }
}

@include RWD_1024 {
  .layout_wrapper {
    .content {
      .item_block {
        .img_block {
          height: 181px;
        }
      }
    }
  }
}

@include RWD_768 {
  ::v-deep(.p-paginator) {
    padding: 0px 0px 50px;
  }

  .layout_wrapper {
    .content {
      margin: 30px -15px 0px -15px;

      .item_block {
        padding: 0px 15px;
        width: 50%;

        .img_block {
          height: 226px;
        }
      }
    }
  }
}

@include RWD_414 {
  .layout_wrapper {
    .content {
      display: block;
      margin: 10px -15px 50px -15px;

      .item_block {
        margin-bottom: 30px;
        width: 100%;

        .img_block {
          height: auto;
        }
      }
    }
  }
}
</style>
